import React, {ReactNode, useContext, useEffect, useMemo, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {doesSessionExist} from 'supertokens-auth-react/recipe/session';

import ContextViewFileList, {I_breadcrumb} from './ContextViewFileList';
import {AccessibleDocument, DirView, DocumentsService, ApiError} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';

interface IContextProviderViewFileListProps {
  children?: ReactNode;
  user_specific?: boolean;
  hausverwaltung?: boolean;
}


const ContextProviderViewFileList = ({
  children,
  user_specific,
  hausverwaltung,
}: IContextProviderViewFileListProps) => {
  const {call_backend_api} = useContext(APIWrapperContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the single source of truth for the current folder. No ?dir
  // means "page root". Browser back/forward change the param and the effect
  // below follows.
  const dirParam = searchParams.get('dir');
  const curr_dir_id = dirParam === null ? undefined : dirParam;

  // '' is the storage root for admin and user views. The Hausverwaltung page's
  // root prefix is env-configured on the backend and only known once a root
  // response arrives.
  const [root_id, setRootId] = React.useState<string | undefined>(
    hausverwaltung ? undefined : ''
  );
  const [curr_dir_view, setCurrDirView] = React.useState<DirView | {}>({});
  const [loading_dir, setLoadingDir] = React.useState<boolean>(false);
  const [accessible_elements, setAccessibleElements] = React.useState<
    Record<string, AccessibleDocument>
  >({});
  const [error, setError] = React.useState<string | null>(null);

  // Visited folders render instantly from this cache while a background
  // refetch revalidates them (stale-while-revalidate).
  const dir_cache = useRef<Map<string, DirView>>(new Map());
  // Monotonic sequence: a slow response for a folder the user already left
  // must not overwrite the newer view.
  const request_seq = useRef(0);

  const update_accessible_elements = () => {
    if (user_specific) {
      return;
    }
    call_backend_api(
      DocumentsService.getAccessibleDocsDocGetAccessibleDocsGet(),
      (response: Record<string, AccessibleDocument>) => {
        setAccessibleElements(response);
      },
      (err: ApiError) => {
        console.error('Error loading accessible documents:', err);
      },
      undefined,
      false,
      undefined,
      undefined,
      true
    );
  };

  const processDirView = (dirView: DirView): DirView => {
    return {
      ...dirView,
      children: dirView.children?.map((child) => ({
        ...child,
        isUnread: child.isUnread || false,
        children: child.children ? processDirView(child).children : [],
      })),
    };
  };

  const fetch_dir = (id: string | undefined, opts?: {rootProbe?: boolean}) => {
    if (!doesSessionExist()) {
      setError('Sie sind nicht eingeloggt.');
      setLoadingDir(false);
      return;
    }

    const rootProbe = opts?.rootProbe === true;
    // A root probe only discovers the Hausverwaltung root id; it must never
    // steal the view from the folder the user actually opened.
    const seq = rootProbe ? -1 : ++request_seq.current;

    let hadCache = false;
    if (!rootProbe) {
      setError(null);
      const cache_key = id !== undefined ? id : root_id;
      const cached = cache_key !== undefined ? dir_cache.current.get(cache_key) : undefined;
      if (cached) {
        hadCache = true;
        setCurrDirView(cached);
        setLoadingDir(false);
      } else {
        setLoadingDir(true);
      }
    }

    const handleSuccess = (response: DirView) => {
      const processed = processDirView(response);
      dir_cache.current.set(response.hidrive_id, processed);
      if (id === undefined) {
        setRootId(response.hidrive_id);
      }
      if (!rootProbe && seq === request_seq.current) {
        setCurrDirView(processed);
        setLoadingDir(false);
      }
    };

    const handleError = (err: ApiError) => {
      if (rootProbe) {
        console.error('Root probe failed:', err);
        return;
      }
      if (seq !== request_seq.current) {
        return;
      }
      if (hadCache) {
        // The user is looking at valid cached content; a failed background
        // revalidation must not cover it with an error alert. (loading_dir is
        // already false for cache hits.)
        console.error('Background revalidation failed:', err);
        return;
      }
      if (err && err.status === 404) {
        setError('Ordner nicht gefunden oder kein Zugriff.');
      } else {
        setError('Laden des Ordners fehlgeschlagen.');
      }
      console.error('Error loading directory:', err);
      setLoadingDir(false);
    };

    if (user_specific) {
      call_backend_api(
        DocumentsService.hidriveGetFilesUserDocGetDirContentUserGet(id),
        handleSuccess,
        handleError,
        undefined,
        false,
        undefined,
        'Laden des Ordners fehlgeschlagen',
        false
      );
    } else {
      call_backend_api(
        DocumentsService.hidriveGetFilesDocGetDirContentGet(
          id,
          hausverwaltung ? hausverwaltung : false
        ),
        handleSuccess,
        handleError,
        undefined,
        false,
        undefined,
        'Laden des Ordners fehlgeschlagen',
        false
      );
    }
  };

  const navigate_to_dir = (id: string) => {
    const isRootTarget = root_id !== undefined && id === root_id;
    // Already there: clicking the current breadcrumb (or Home at root) must
    // not push a no-op history entry the back button would have to step
    // through again.
    if (id === curr_dir_id || (isRootTarget && curr_dir_id === undefined)) {
      return;
    }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (isRootTarget) {
        // Root is the canonical no-param URL.
        p.delete('dir');
      } else {
        p.set('dir', id);
      }
      return p;
    });
  };

  // Recovery action (error alert, Home): must work even when root_id is
  // unknown (failed Hausverwaltung root probe) or the error happened at the
  // root itself — the two corners navigate_to_dir's no-op guard would swallow.
  const navigate_to_page_root = () => {
    if (curr_dir_id === undefined) {
      // Already at root: nothing to navigate to — retry the fetch instead.
      invalidate_dir_cache();
      fetch_dir(undefined);
      return;
    }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('dir');
      return p;
    });
  };

  const navigate_up = () => {
    if (curr_dir_id === undefined) {
      return;
    }
    const trimmed = curr_dir_id.replace(/\/+$/, '');
    const idx = trimmed.lastIndexOf('/');
    const parent = idx === -1 ? '' : trimmed.slice(0, idx + 1);
    if (root_id !== undefined && (parent === root_id || parent.length < root_id.length)) {
      navigate_to_dir(root_id);
    } else {
      navigate_to_dir(parent);
    }
  };

  const invalidate_dir_cache = () => {
    dir_cache.current.clear();
  };

  const update_dir_view = (id: string | undefined) => {
    fetch_dir(id);
  };

  // Breadcrumbs derived from the S3 path key (path-as-identity since the
  // MinIO migration), relative to the page root. Works for deep links and F5.
  const breadcrumbs = useMemo<I_breadcrumb[]>(() => {
    if (root_id === undefined || curr_dir_id === undefined) {
      return [];
    }
    let relative = curr_dir_id;
    if (root_id !== '') {
      if (!curr_dir_id.startsWith(root_id)) {
        // Cross-scope deep link (e.g. /hausverwaltung?dir=Aushänge/Foo/):
        // root-relative crumb ids would point at nonexistent paths, so show
        // no breadcrumbs at all; the view itself still works.
        return [];
      }
      relative = curr_dir_id.slice(root_id.length);
    }
    const segments = relative.split('/').filter((s) => s !== '');
    return segments.map((name, i) => ({
      id: root_id + segments.slice(0, i + 1).join('/') + '/',
      name,
    }));
  }, [root_id, curr_dir_id]);

  // Load whenever the URL-selected folder changes (mount, click, back/forward).
  useEffect(() => {
    fetch_dir(curr_dir_id);
  }, [curr_dir_id]);

  useEffect(() => {
    update_accessible_elements();
    // Hausverwaltung deep link: discover the page root id in the background so
    // breadcrumbs and the Home button work without navigating there first.
    if (hausverwaltung && curr_dir_id !== undefined) {
      fetch_dir(undefined, {rootProbe: true});
    }
  }, []);

  return (
    <ContextViewFileList.Provider
      value={{
        curr_dir_id,
        root_id,
        breadcrumbs,
        curr_dir_view,
        accessible_elements,
        user_specific,
        loading_dir,
        error,
        update_accessible_elements,
        navigate_to_dir,
        navigate_to_page_root,
        navigate_up,
        update_dir_view,
        invalidate_dir_cache,
      }}>
      {children}
    </ContextViewFileList.Provider>
  );
};

export default ContextProviderViewFileList;
